import * as Popover from '@radix-ui/react-popover';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Loader2, MessageSquareQuote, Search } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type KeyboardEvent } from 'react';
import { get, post, type ApiError } from '../../lib/api';
import { cn } from '../../lib/cn';
import { Button } from '../../components/ui';

/**
 * Canned responses, in the composer.
 *
 * The administration screen promised "stock wording agents can insert" and a
 * shortcut "agents type this to insert it", and the composer had no way to
 * insert anything: only the single default reply was ever used. Now there is an
 * "Insert response" picker beside Attach, and typing `/` at the start of a line
 * opens the same list filtered by what follows — `/vpn` finds the response
 * whose shortcut or title matches.
 *
 * A response is rendered on the server against this ticket, because the
 * placeholder values are fields the browser may not hold. A placeholder the
 * ticket cannot fill comes back named, and stays visible in the text, so the
 * agent sees exactly what is left to write before sending.
 */

export interface CannedResponse {
  id: number;
  title: string;
  shortcut: string | null;
  folder_name: string | null;
}

export interface RenderedResponse {
  text: string;
  unresolved: string[];
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** Whether a response is one the typed words name — the same test the server's search applies. */
function matches(response: CannedResponse, query: string): boolean {
  if (!query) return true;
  const wanted = query.toLocaleLowerCase();
  return (
    response.title.toLocaleLowerCase().includes(wanted) ||
    (response.shortcut ?? '').toLocaleLowerCase().includes(wanted)
  );
}

/**
 * The responses the typed words name.
 *
 * The search is sent a moment after typing stops, so the list it answers is
 * always a little behind what is in the box. Read straight, that meant Enter
 * straight after "/pwd" picked the first of the previous, unfiltered list, and
 * once the search was sent the list blinked to "Loading" — and Enter went into
 * the reply as a new line. The last answer is kept while the next is on its
 * way, and whatever is shown is narrowed to what is typed now, so the list and
 * Enter always agree with the box.
 */
export function useCannedResponses(query: string, enabled: boolean) {
  const typed = query.trim();
  const debounced = useDebounced(typed, 150);
  const result = useQuery<{ data: CannedResponse[] }, ApiError>({
    queryKey: ['canned-responses', debounced],
    queryFn: () => get('/canned-responses', debounced ? { q: debounced } : undefined),
    enabled,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
  const rows = useMemo(
    () => (result.data?.data ?? []).filter((response) => matches(response, typed)),
    [result.data, typed],
  );
  // The answer for what is typed now has not arrived yet.
  const searching = result.isPlaceholderData || debounced !== typed;
  return { ...result, rows, searching };
}

export async function renderResponse(id: number, ticketNumber: number): Promise<RenderedResponse> {
  const result = await post<{ data: { body_text: string }; meta: { unresolved: string[] } }>(
    `/canned-responses/${id}/render`,
    { ticket_number: ticketNumber },
  );
  return { text: result.data.body_text, unresolved: result.meta?.unresolved ?? [] };
}

/** The list itself, shared by the button's popover and the `/` shortcut. */
export function ResponseList({
  query,
  activeIndex,
  onActiveIndexChange,
  onPick,
  listId,
  enabled = true,
}: {
  query: string;
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  onPick: (response: CannedResponse) => void;
  listId: string;
  enabled?: boolean;
}) {
  const responses = useCannedResponses(query, enabled);
  const rows = responses.rows;

  // Grouped by folder, in the order the server ranked them (most used first).
  const groups = useMemo(() => {
    const out: Array<{ folder: string; items: Array<{ response: CannedResponse; index: number }> }> = [];
    rows.forEach((response, index) => {
      const folder = response.folder_name ?? 'Responses';
      let entry = out.find((candidate) => candidate.folder === folder);
      if (!entry) {
        entry = { folder, items: [] };
        out.push(entry);
      }
      entry.items.push({ response, index });
    });
    return out;
  }, [rows]);

  if (responses.isLoading || (rows.length === 0 && responses.searching && !responses.isError)) {
    return (
      <p className="flex items-center gap-2 px-2 py-2 text-body text-ink-subtle">
        <Loader2 aria-hidden className="size-3.5 animate-spin" /> Loading responses…
      </p>
    );
  }
  if (responses.isError) {
    return (
      <p role="alert" className="px-2 py-2 text-body text-critical-ink">
        {responses.error.status === 403 ? 'You can’t use canned responses.' : 'The responses could not be loaded.'}
      </p>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="px-2 py-2 text-body text-ink-subtle">
        {query.trim() ? 'No response matches that.' : 'No canned responses have been written yet.'}
      </p>
    );
  }

  return (
    <div id={listId} role="listbox" aria-label="Canned responses">
      {groups.map((group) => (
        <div key={group.folder} role="group" aria-label={group.folder}>
          <p className="px-2 pt-2 pb-1 text-mini font-medium text-ink-subtle">{group.folder}</p>
          {group.items.map(({ response, index }) => (
            <div
              key={response.id}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              onMouseMove={() => onActiveIndexChange(index)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onPick(response)}
              className={cn(
                'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-body',
                index === activeIndex ? 'bg-sunken text-ink' : 'text-ink',
              )}
            >
              <span dir="auto" className="min-w-0 flex-1 truncate">
                {response.title}
              </span>
              {response.shortcut && (
                <kbd className="shrink-0 rounded border border-line bg-surface px-1 font-mono text-mini text-ink-subtle">
                  /{response.shortcut}
                </kbd>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** The rows the list currently holds, so a keyboard handler can pick by index. */
export function useResponseRows(query: string, enabled: boolean): CannedResponse[] {
  return useCannedResponses(query, enabled).rows;
}

/** "Insert response", beside Attach. */
export function InsertResponseButton({
  onPick,
  disabled,
}: {
  onPick: (response: CannedResponse) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listId = useId();
  const rows = useResponseRows(query, open);

  useEffect(() => setActive(0), [query, open]);

  const pick = (response: CannedResponse) => {
    setOpen(false);
    setQuery('');
    onPick(response);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((current) => Math.min(current + 1, Math.max(rows.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const chosen = rows[active];
      if (chosen) pick(chosen);
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button size="sm" variant="ghost" disabled={disabled}>
          <MessageSquareQuote aria-hidden className="size-3.5" />
          Insert response
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className="animate-pop z-[70] w-80 max-w-[calc(100vw-2rem)] rounded-card border border-line bg-raised p-1 shadow-float"
        >
          <div className="flex items-center gap-2 border-b border-line px-2 pb-1.5">
            <Search aria-hidden className="size-3.5 shrink-0 text-ink-subtle" />
            <input
              autoFocus
              dir="auto"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onKeyDown}
              role="combobox"
              aria-expanded
              aria-controls={listId}
              aria-activedescendant={rows.length > 0 ? `${listId}-${active}` : undefined}
              aria-label="Search canned responses"
              placeholder="Search by title or shortcut…"
              className="h-7 min-w-0 flex-1 bg-transparent text-body text-ink outline-none placeholder:text-ink-subtle"
            />
          </div>
          <div className="scrollbar-slim max-h-72 overflow-y-auto">
            <ResponseList
              query={query}
              activeIndex={active}
              onActiveIndexChange={setActive}
              onPick={pick}
              listId={listId}
              enabled={open}
            />
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/**
 * The `/shortcut` at the caret, if the caret is at the end of one that starts
 * a line. Returns the text after the slash and where the token begins, so the
 * token can be replaced by the response it picks.
 */
export function slashTokenAt(value: string, caret: number): { query: string; start: number } | null {
  const before = value.slice(0, caret);
  const match = /(^|\n)\/([\p{L}\p{N}_-]*)$/u.exec(before);
  if (!match) return null;
  const query = match[2] ?? '';
  return { query, start: caret - query.length - 1 };
}
